import { useCallback, useState } from "react";

// The click-to-filter plumbing shared by every panel that can narrow the list,
// parameterised by which panel raised the filter. Built once here so the admin
// and client pages behave identically and a second panel costs nothing.
//
// WHY IT MATCHES ON TEXT, WHICH IS A COMPROMISE.
// `fn_get_conversation_topics` returns aggregates — {topic, count,
// pct_of_conversations} and {question, count} — and no conversation ids. Its
// topic vocabulary ("dump_station", "pet_policy") shares not one value with any
// column on a list row either: `primary_intent` is free text, and across all 78
// chat conversations in the Phase 0 window the two sets do not intersect. There
// is no key to join on, so the bar narrows the list the only way available to a
// render-only frontend — by the words themselves, against the text each row
// already carries.
//
// The consequence is honest but real: a topic whose words do not appear in any
// row's summary narrows the list to nothing. The chip says exactly what was
// filtered and removes it in one click, so an empty list reads as a filter
// rather than as missing data. Closing the gap properly is a BACKEND change —
// either topic rows carrying their conversation ids, or a p_topic parameter on
// fn_get_conversation_list — and it is filed as such rather than approximated
// harder here.
//
// The filter is its own predicate, NOT the search box: raising one must not
// silently wipe what a reader has typed, and the two compose.

const WORD_SPLIT = /[^a-z0-9]+/;

// Words that carry no meaning to match on. A question is a whole sentence, and
// requiring every word of "Do you have a dump station?" to appear in a summary
// matched nothing — the summary says "asked whether the resort has a dump
// station", which is the same question in different grammar. Stripping the
// grammar leaves the two nouns that actually identify the topic.
//
// Deliberately short. A longer list starts discarding words that ARE the
// question ("pets", "rates"), and the filter widens instead of sharpening.
const STOPWORDS = new Set([
  'the', 'and', 'for', 'are', 'you', 'your', 'yours', 'have', 'has', 'had',
  'can', 'could', 'would', 'should', 'will', 'does', 'did', 'was', 'were',
  'this', 'that', 'these', 'those', 'there', 'here', 'what', 'when', 'where',
  'which', 'who', 'whom', 'how', 'any', 'with', 'from', 'into', 'about',
  'get', 'got', 'need', 'want', 'like', 'please', 'thanks', 'hello',
]);

/** The lowercased text a row can be matched against. Never derived, only read. */
const rowText = (row) => [
  row?.convo?.summary,
  row?.intel?.summary,
  row?.intel?.primary_intent,
  row?.intel?.outcome,
  row?.intel?.stay_type_sought,
  row?.tag,
  row?.displayName,
  row?._searchIdx,
].filter(Boolean).join(" ").toLowerCase();

/**
 * Does this row match a raised filter?
 *
 * A `conversations` filter carries conversation ids and matches on identity.
 * Everything else matches on words, because the panel that raised it has no ids
 * to offer.
 *
 * Every word of the topic must appear somewhere in the row's text — order does
 * not matter, so "after_hours_arrival" matches a summary that says the visitor
 * arrived after hours. Words of two characters or fewer are dropped: they match
 * everywhere and would widen the filter to the whole list.
 *
 * A question matches on its own words the same way. Requiring the sentence
 * verbatim matched almost nothing, because the extraction rewrites punctuation.
 */
export const matchesPanelFilter = (row, filter) => {
  if (!filter?.value) return true;

  // An exact id selection. `fn_get_knowledge_gaps` rows carry the conversation
  // ids they were derived from, so a gap can select its own conversations
  // instead of guessing at their wording — this branch cannot miss the way the
  // word matching below can, and is preferred wherever ids are available.
  if (filter.kind === "conversations") {
    const ids = Array.isArray(filter.value) ? filter.value : [filter.value];
    if (ids.length === 0) return true;
    const rowId = row?.id ?? row?.convo?.id ?? row?.intel?.conversation_id;
    return ids.includes(rowId);
  }

  const words = String(filter.value)
    .toLowerCase()
    .split(WORD_SPLIT)
    .filter((w) => w.length > 2 && !STOPWORDS.has(w));
  if (words.length === 0) return true;
  const text = rowText(row);
  return words.every((w) => text.includes(w));
};

/** Apply a raised filter to a list of rows. Pure; returns a new array. */
export const applyPanelFilterTo = (rows, filter) => {
  if (!filter?.value) return rows;
  return (Array.isArray(rows) ? rows : []).filter((r) => matchesPanelFilter(r, filter));
};

/**
 * @param {{current: (HTMLElement|null)}} [listRef] scrolled into view when a
 *   filter is raised, so the effect of a click made far above the list is seen
 */
export const usePanelFilter = (listRef) => {
  const [panelFilter, setPanelFilter] = useState(null);

  const applyPanelFilter = useCallback((filter) => {
    // Clicking the active bar again clears it — the control is a toggle, which
    // is what `aria-pressed` on the bar promises.
    setPanelFilter((current) => {
      const same = current && current.kind === filter?.kind && current.value === filter?.value;
      return same ? null : filter;
    });
    const node = listRef?.current;
    if (node && typeof node.scrollIntoView === "function") {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [listRef]);

  const clearPanelFilter = useCallback(() => setPanelFilter(null), []);

  return { panelFilter, applyPanelFilter, clearPanelFilter };
};

export default usePanelFilter;
